/**
 * FEEDBACK (C1) — o link individual, SEM login, pensado para o celular (mesmo padrão do `TestePublicoPage`):
 *  - a AUTOAVALIAÇÃO ("Mapeamento de Função" do antigo): abertura, o formulário inteiro numa página, salvo no
 *    SERVIDOR a cada resposta (fechar e reabrir continua de onde parou), e "Enviar respostas";
 *  - o PLANO (link de 7 dias de quem não tem login): só leitura, e o PDF.
 *
 * Textos: os do antigo vêm do servidor (hero, perguntas, envio, tela de obrigado, rodapé); os de link vencido/encerrado
 * são os aprovados (L1–L3). Obrigatória = `required` nativo, como no antigo.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { Alert, Button, Card } from '@/components/ui';
import { FormularioDeSecoes } from '@/components/feedback/FormularioDeSecoes';
import { PlanoPublicado } from '@/components/feedback/PlanoPublicado';
import { feedbackPublico, type EstadoPublicoFeedback, type SecaoDoFormulario } from '@/lib/feedback/feedback-store';
import { perguntaVisivel, type Respostas } from '@/lib/feedback/formulario';
import { FB } from '@/lib/feedback/textos';
import { TEXTOS as TEXTOS_TESTES } from '@/lib/testes/textos';

const LP = TEXTOS_TESTES.link_publico;

function Moldura({ titulo, rodape, children }: { titulo?: string; rodape?: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-paper px-3 py-6 dark:bg-paper-dark md:px-4 md:py-10">
      <div className="mx-auto max-w-2xl">
        <Card className="overflow-hidden border-ink-1/10 bg-paper dark:border-ink-dark-1/10 dark:bg-paper-dark">
          <div className="border-b border-ink-1/10 p-5 dark:border-ink-dark-1/10 md:p-6">
            <Logo size="md" className="h-10 md:h-12" />
            {titulo && <p className="mt-2 text-xs font-semibold uppercase tracking-[0.1em] text-ink-3">{titulo}</p>}
          </div>
          <div className="p-5 md:p-6">{children}</div>
        </Card>
        {rodape && <p className="mt-4 text-center text-xs text-ink-3">{rodape}</p>}
      </div>
    </div>
  );
}

function Mensagem({ texto }: { texto: string }) {
  return (
    <Moldura>
      <p className="text-base text-ink-1 dark:text-ink-dark-1" data-testid="feedback-mensagem">{texto}</p>
    </Moldura>
  );
}

/** As etapas que somem quando o fluxo delas é desmarcado (o antigo não enviava o que estava escondido). */
function etapasQueSomem(secoes: SecaoDoFormulario[], antes: Respostas, depois: Respostas): string[] {
  return secoes
    .flatMap((s) => s.perguntas)
    .filter((p) => p.condicional_a && perguntaVisivel(p, antes) && !perguntaVisivel(p, depois) && antes[p.id] !== undefined)
    .map((p) => p.id);
}

export function FeedbackPublicoPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [estado, setEstado] = useState<EstadoPublicoFeedback | null>(null);
  const [falha, setFalha] = useState(false);
  const [comecou, setComecou] = useState(false);
  const [respostas, setRespostas] = useState<Respostas>({});
  const [erroAoSalvar, setErroAoSalvar] = useState(false);
  const [erroAoEnviar, setErroAoEnviar] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const aplicar = useCallback((e: EstadoPublicoFeedback) => {
    setEstado(e);
    if (e.estado === 'respondendo') {
      setRespostas(e.respostas);
      // quem já começou (tem resposta salva) volta direto ao formulário
      if (Object.keys(e.respostas).length > 0) setComecou(true);
    }
  }, []);

  useEffect(() => {
    feedbackPublico.estado(token).then(aplicar).catch(() => setFalha(true));
  }, [token, aplicar]);

  if (falha) return <Mensagem texto={FB.link_encerrado} />;
  if (!estado) {
    return (
      <Moldura>
        <div className="h-24 animate-pulse rounded bg-paper-warm dark:bg-paper-dark-warm" aria-busy="true" />
      </Moldura>
    );
  }
  if (estado.estado === 'vencido') return <Mensagem texto={FB.link_vencido} />;
  if (estado.estado === 'muitas_tentativas') return <Mensagem texto={LP.muitas_tentativas} />;
  // "indisponivel" (o feedback desligado) usa a mesma frase neutra: o link existe, mas não abre agora.
  if (estado.estado === 'encerrado' || estado.estado === 'indisponivel') return <Mensagem texto={FB.link_encerrado} />;

  // ── O plano (só leitura) ─────────────────────────────────────────────────
  if (estado.estado === 'plano') {
    return (
      <Moldura titulo={estado.rodada}>
        <div className="space-y-6">
          <h1 className="font-display text-2xl font-medium text-ink-1 dark:text-ink-dark-1" data-testid="plano-publico">{FB.titulo_plano_publico}</h1>
          <PlanoPublicado plano={estado.publicado} rotulos={estado.rotulos} />
          <Button size="lg" className="w-full md:w-auto" onClick={() => window.location.assign(feedbackPublico.caminhoDoPdf(token))} data-testid="plano-pdf">
            {FB.botao_pdf}
          </Button>
        </div>
      </Moldura>
    );
  }

  // ── Enviada (a tela de obrigado do antigo) ───────────────────────────────
  if (estado.estado === 'enviada') {
    const ob = estado.envio.tela_obrigado;
    return (
      <Moldura>
        <div className="space-y-3" data-testid="autoavaliacao-enviada">
          <h1 className="font-display text-2xl font-medium text-ink-1 dark:text-ink-dark-1">{ob.titulo}</h1>
          <p className="text-base text-ink-1 dark:text-ink-dark-1">{ob.texto}</p>
        </div>
      </Moldura>
    );
  }

  if (estado.estado !== 'respondendo') return <Mensagem texto={FB.link_encerrado} />;
  const { hero, envio, secoes } = estado;

  // ── Abertura (o "hero" do antigo) ────────────────────────────────────────
  if (!comecou) {
    return (
      <Moldura titulo={hero.eyebrow} rodape={envio.rodape}>
        <div className="space-y-4 text-base leading-relaxed text-ink-1 dark:text-ink-dark-1" data-testid="autoavaliacao-abertura">
          <h1 className="font-display text-2xl font-medium">{hero.titulo}</h1>
          <p className="text-sm text-ink-2 dark:text-ink-dark-2">{estado.nomeCompleto}</p>
          <p>{hero.texto}</p>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-2 dark:text-ink-dark-2">
            {hero.meta.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
          <Button size="lg" className="w-full" onClick={() => setComecou(true)} data-testid="autoavaliacao-comecar">{hero.botao}</Button>
        </div>
      </Moldura>
    );
  }

  async function responder(chave: string, valor: string | string[] | null) {
    const depois = { ...respostas, [chave]: valor };
    const somem = etapasQueSomem(secoes, respostas, depois);
    for (const k of somem) delete depois[k];
    setRespostas(depois);
    try {
      for (const [k, v] of [[chave, valor] as const, ...somem.map((k) => [k, null] as const)]) {
        const r = await feedbackPublico.responder(token, k, v);
        if ('estado' in r) {
          aplicar(r);
          return;
        }
      }
      setErroAoSalvar(false);
    } catch {
      setErroAoSalvar(true);
    }
  }

  async function enviar() {
    setEnviando(true);
    setErroAoEnviar(false);
    try {
      aplicar(await feedbackPublico.enviar(token));
    } catch {
      setErroAoEnviar(true);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Moldura titulo={hero.eyebrow} rodape={envio.rodape}>
      <form
        className="space-y-8"
        data-testid="autoavaliacao-formulario"
        onSubmit={(e) => {
          e.preventDefault();
          void enviar();
        }}
      >
        <FormularioDeSecoes secoes={secoes} respostas={respostas} aoMudar={(k, v) => void responder(k, v)} />
        {erroAoSalvar && <Alert variant="danger">{LP.falha_ao_salvar}</Alert>}
        <div className="space-y-3">
          <p className="text-sm text-ink-2 dark:text-ink-dark-2">{envio.texto_antes_do_botao}</p>
          {erroAoEnviar && <Alert variant="danger">{envio.erro}</Alert>}
          <Button type="submit" size="lg" className="w-full" disabled={enviando} data-testid="autoavaliacao-enviar">
            {enviando ? envio.botao_enviando : envio.botao}
          </Button>
        </div>
      </form>
    </Moldura>
  );
}
